import React, { FC, useState, Fragment } from 'react'
import classNames from 'classnames'

import { IconMoon, Typography } from '@/atoms'
import { Spacer } from '@/layout'
import {
  ChecklistCardTitleProps,
  ChecklistCardBodyProps,
  ChecklistCardProps,
  ChecklistCardLinkProps,
  ChecklistCardItemsProps,
  ChecklistCardItemProps,
} from '@/types'
import { getStyledElementProps } from '@/utils'
import styles from './checklist-card.module.scss'

export const ChecklistCardTitle: FC<ChecklistCardTitleProps> = ({
  children,
  ...props
}) => {
  return (
    <div className={styles['title']}>
      <Typography variant={'headline-1'} typographyColor={'gray-8'} {...props}>
        {children}
      </Typography>
    </div>
  )
}

export const ChecklistCardBody: FC<ChecklistCardBodyProps> = ({
  children,
  ...props
}) => {
  return (
    <div className={styles['body']}>
      <Typography
        variant={'body-1'}
        tag={'span'}
        typographyColor={'gray-6'}
        {...props}
      >
        {children}
      </Typography>
    </div>
  )
}

export const ChecklistCardItems: FC<ChecklistCardItemsProps> = ({
  items,
  ...props
}) => {
  const getIconName = (
    iconName: string | undefined | null,
    defaultIcon: string
  ) => {
    if (iconName) {
      return iconName
    } else {
      return defaultIcon
    }
  }
  return (
    <div className={styles['items']}>
      {items?.map((item: ChecklistCardItemProps, index: number) => (
        <Fragment key={index}>
          <div className={styles['item']}>
            <IconMoon
              name={getIconName(item?.iconName, 'icon-check-green')}
              size={'md'}
              style={{ paddingTop: '4px' }}
            />
            <Typography
              variant={'body-1'}
              tag={'span'}
              typographyColor={'gray-6'}
              {...props}
            >
              {item?.name}
            </Typography>
          </div>
          <br />
        </Fragment>
      ))}
    </div>
  )
}

export const ChecklistCardLink: FC<ChecklistCardLinkProps> = ({
  children,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const handleMouseEnter = () => {
    setIsHovered(true)
  }
  const handleMouseLeave = () => {
    setIsHovered(false)
  }
  const getIcon = () =>
    isHovered && <IconMoon name={'icon-arrow-red-1'} size={'xs'} />
  return (
    <div
      className={styles['link']}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Typography
        variant={'body-1'}
        tag={'span'}
        typographyColor={'red-5'}
        {...props}
      >
        {children}
      </Typography>
      {getIcon()}
    </div>
  )
}

export const ChecklistCard: FC<ChecklistCardProps> = ({
  children,
  variant = 'default',
  image,
  alt = '',
  width = '40',
  height = '40',
  loading = 'lazy',
  ...props
}) => {
  const styledProps = getStyledElementProps(
    props,
    classNames(styles['root'], styles[variant])
  )
  return (
    <div {...styledProps}>
      {image && (
        <img
          className={styles['image']}
          src={image}
          alt={alt}
          width={width}
          height={height}
          loading={loading}
        />
      )}
      {children}
      <Spacer variant={'horizontal'} size={'8x'} />
    </div>
  )
}
